import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';

/**
 * Wiersz listy (DESIGN §4): 44–60 px, linia border/subtle pod spodem, bez kart.
 * `unread` = pogrubiony, ciemniejszy tytuł (nieprzeczytane / do zrobienia).
 * `trailing` zajmuje najwyżej 60% wiersza: długi tekst w nim (np. nazwa klienta z `min-w-0 truncate`) skraca się
 * wielokropkiem, zamiast wypychać tytuł i poszerzać stronę.
 * Telefon (< 768 px): tytuł w najwyżej 3 liniach zamiast jednej z wielokropkiem (przegląd Fali 9 — w białej sekcji
 * „Odnowienie rea.pl za 25 dni” traciło liczbę dni); desktop: jedna linia.
 */
export function ListRow({
  leading,
  title,
  description,
  meta,
  trailing,
  to,
  onClick,
  chevron = false,
  unread = false,
  height = 'm',
  className,
}: {
  leading?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  to?: string;
  onClick?: () => void;
  chevron?: boolean;
  unread?: boolean;
  height?: 's' | 'm' | 'l';
  className?: string;
}) {
  const content = (
    <>
      {leading && <span className="flex shrink-0 items-center">{leading}</span>}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          data-list-title=""
          className={cn(
            'text-body-s break-words max-md:line-clamp-3 md:truncate',
            unread ? 'font-semibold text-fg' : 'font-medium text-fg',
          )}
        >
          {title}
        </span>
        {description && <span className="truncate text-caption text-fg-muted">{description}</span>}
      </span>
      {meta && <span className="shrink-0 text-body-s whitespace-nowrap text-fg-muted">{meta}</span>}
      {trailing && <span className="flex max-w-[60%] min-w-0 shrink-0 items-center gap-3.5">{trailing}</span>}
      {chevron && <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />}
    </>
  );

  const classes = cn(
    'flex w-full items-center gap-3.5 border-b border-line text-left',
    height === 's' && 'min-h-11 py-2',
    height === 'm' && 'min-h-[52px] py-2',
    height === 'l' && 'min-h-[60px] py-2.5',
    (to || onClick) &&
      'transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
    className,
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={classes}>
        {content}
      </button>
    );
  }
  return <div className={classes}>{content}</div>;
}
